import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Landmark, AlertCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";
const SEPA_FEE = 0.80;

const cardStyle = {
  ...hostCardStyle,
  width: 'calc(100% - 32px)',
  maxWidth: 360,
  padding: '18px 18px',
  boxSizing: 'border-box',
};

const isValidIban = (raw) => {
  const cleaned = (raw || '').replace(/\s+/g, '').toUpperCase();
  if (cleaned.length < 15 || cleaned.length > 34) return false;
  return /^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned);
};

const formatIban = (raw) => (raw || '').replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

export default function VenueConnectIbanPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const { activeVenueAppId } = useVenue();
  const [iban, setIban] = useState('');
  const [holderName, setHolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentMethod, setCurrentMethod] = useState(location.state?.previousMethod ?? null);
  const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);

  const getToken = useCallback(() => localStorage.getItem('token'), []);

  useEffect(() => {
    if (!activeVenueAppId) return;
    axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then((res) => {
      setCurrentMethod(res.data?.payout_method);
      if (res.data?.iban_holder_name) setHolderName(res.data.iban_holder_name);
    }).catch(() => {});
  }, [activeVenueAppId, getToken]);

  const valid = isValidIban(iban) && holderName.trim().length >= 2;
  const wasVivaBefore = currentMethod === 'viva_wallet';

  const handleSave = async () => {
    if (!valid || !activeVenueAppId) return;
    if (wasVivaBefore) {
      setConfirmSwitchOpen(true);
      return;
    }
    await performSave();
  };

  const performSave = async () => {
    setConfirmSwitchOpen(false);
    setSaving(true);
    try {
      await axios.post(`${API}/venue/payout-method/iban`, {
        venue_id: activeVenueAppId,
        iban: iban.replace(/\s+/g, ''),
        holder_name: holderName.trim(),
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('IBAN connected');
      navigate('/venue/settings/payouts');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect IBAN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-shell" data-testid="venue-connect-iban-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button data-testid="venue-connect-iban-back" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-connect-iban-logo" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(37,99,235,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Landmark size={20} color="#2563EB" strokeWidth={2.2} />
        </div>
        <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Connect Bank Account</h2>
      </div>

      {/* Mandatory SEPA fee disclosure */}
      <div data-testid="venue-connect-iban-fee-disclosure" style={{
        width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 14,
        padding: '12px 14px', borderRadius: 14,
        background: 'rgba(245, 158, 11, 0.10)', border: '1px solid rgba(245, 158, 11, 0.32)',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <AlertTriangle size={20} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--status-warning-text)', margin: 0 }}>
            €{SEPA_FEE.toFixed(2)} fee per transaction
          </p>
          <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text)', margin: '4px 0 0', lineHeight: 1.3 }}>
            SEPA payouts cost €{SEPA_FEE.toFixed(2)} each — this is deducted from your profit on every transaction.
            Funds arrive in <strong>1–2 business days</strong>.
            {' '}<button
              onClick={() => navigate('/venue/settings/payouts/connect-viva')}
              data-testid="venue-connect-iban-switch-to-viva-link"
              style={{ background: 'none', border: 'none', color: 'var(--status-success-text)', fontFamily: font, fontSize: 13, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
            >
              Use Viva Wallet instead (free)
            </button>
          </p>
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 14, padding: '18px' }}>
        <label style={{ display: 'block', fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          IBAN
        </label>
        <input
          data-testid="venue-connect-iban-input"
          type="text"
          value={formatIban(iban)}
          onChange={(e) => setIban(e.target.value)}
          placeholder="DE89 3704 0044 0532 0130 00"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12,
            border: '1px solid var(--card-border)', fontFamily: 'monospace',
            fontSize: 15, boxSizing: 'border-box', textTransform: 'uppercase',
          }}
        />
        {iban && !isValidIban(iban) && (
          <p data-testid="venue-connect-iban-error" style={{ fontFamily: font, fontSize: 13, color: 'var(--status-danger-text)', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertCircle size={14} /> Doesn&apos;t look like a valid IBAN — check the country prefix and length.
          </p>
        )}

        <label style={{ display: 'block', fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', marginTop: 14, marginBottom: 8 }}>
          Account holder name
        </label>
        <input
          data-testid="venue-connect-iban-holder-input"
          type="text"
          value={holderName}
          onChange={(e) => setHolderName(e.target.value)}
          placeholder="Your business name or full legal name"
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 12,
            border: '1px solid rgba(0,0,0,0.15)', fontFamily: font,
            fontSize: 15, boxSizing: 'border-box',
          }}
        />
      </div>

      <button
        data-testid="venue-connect-iban-submit"
        onClick={handleSave}
        disabled={!valid || saving}
        style={{
          width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16,
          padding: '14px 0', borderRadius: 16, border: 'none',
          background: !valid || saving ? 'var(--accent-surface)' : '#2563EB',
          color: '#FFFFFF', fontFamily: font, fontSize: 20, fontWeight: 700,
          cursor: !valid || saving ? 'not-allowed' : 'pointer',
        }}
      >
        {saving ? 'Connecting…' : `Connect IBAN (€${SEPA_FEE.toFixed(2)}/tx)`}
      </button>

      <AlertDialog open={confirmSwitchOpen} onOpenChange={setConfirmSwitchOpen}>
        <AlertDialogContent
          data-testid="venue-connect-iban-switch-confirm-dialog"
          style={{ fontFamily: font }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: font, color: 'var(--text)' }}>
              Switch from Viva Wallet to IBAN?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontFamily: font, color: 'var(--text-2)' }}>
              Heads up: switching to IBAN adds a €{SEPA_FEE.toFixed(2)} SEPA fee on every transaction. Funds arrive in 1–2 business days instead of instantly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="venue-connect-iban-switch-cancel"
              style={{ fontFamily: font }}
            >
              Keep Viva Wallet
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="venue-connect-iban-switch-confirm"
              onClick={performSave}
              style={{
                fontFamily: font,
                background: '#2563EB',
                color: '#FFFFFF',
              }}
            >
              Continue to IBAN
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
